import React, { useContext } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	IconButton,
	Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { CreatorContext } from "../../context/CreatorContext";
import { useTranslation } from "react-i18next";
import { EyeOutline } from "react-ionicons";
import Map from "../../assets/showcases/mapa.png";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/X";
import PinterestIcon from "@mui/icons-material/Pinterest";
import chroma from "chroma-js";

const ShowcaseContact = ({ contact }) => {
	const { creator } = useContext(CreatorContext);
	const { t } = useTranslation();

	const CustomTextField = ({ label, type, multiline }) => {
		const getDynamicColor = (background) => {
			if (creator.themeMode === "Dark" && creator.inputType === "input3") {
				return chroma(background).brighten(0.4).hex();
			} else if (
				creator.themeMode === "Light" &&
				creator.inputType === "input3"
			) {
				return chroma(background).darken(0.12).hex();
			} else if (creator.themeMode === "Dark") {
				return chroma(background).brighten(1.5).hex();
			} else {
				return chroma(background).darken(1).hex();
			}
		};

		const backgroundColor = getDynamicColor(creator.themeColor);
		const borderColor = getDynamicColor(creator.themeColor);
		const placeholderTextColor =
			creator.themeMode === "Dark"
				? chroma(creator.themeColor).brighten(2).hex()
				: chroma(creator.themeColor).darken(2).hex();

		return (
			<Box sx={{ marginBottom: "8px" }}>
				{/* Etiqueta del campo */}

				{type === "outline" && (
					<Box
						sx={{
							border: `1px solid ${borderColor}`,
							backgroundColor: creator.themeColor,
							borderRadius: "4px",
							height: multiline ? "200px" : "30px",
							display: "flex",
							alignItems: "flex-start",
							padding: "0 12px",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: placeholderTextColor,
								marginTop: 1,
							}}
						>
							{label}
						</Typography>
					</Box>
				)}

				{type === "filled" && (
					<Box
						sx={{
							backgroundColor: backgroundColor,
							borderRadius: "4px",
							height: multiline ? "200px" : "30px",
							display: "flex",
							padding: "0 12px",
							border: "1px solid transparent",
							alignItems: "flex-start",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: placeholderTextColor,
								marginTop: 1,
							}}
						>
							{label}
						</Typography>
					</Box>
				)}

				{type === "standard" && (
					<Box
						sx={{
							borderBottom: `1px solid ${borderColor}`,
							backgroundColor: "transparent",
							height: multiline ? "200px" : "30px",
							display: "flex",
							padding: "0 0px",
							alignItems: "flex-start",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: placeholderTextColor,
								marginTop: 1,
							}}
						>
							{label}
						</Typography>
					</Box>
				)}
			</Box>
		);
	};

	const contactData = {
		address: t("contact.address"),
		phone: t("contact.phone"),
		email: t("contact.email"),
		hours: [
			t("contact.hours.mon"),
			t("contact.hours.tue"),
			t("contact.hours.wed"),
			t("contact.hours.thu"),
			t("contact.hours.fri"),
			t("contact.hours.sat"),
		],
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				"& *": {
					fontFamily: creator.fontFamilyCap + "!important",
				},
			}}
		>
			{contact &&
				(contact.value === "contact1" || contact.value === "contact2") && (
					<Box
						sx={{
							height:
								creator.navigationType === "Drawer" &&
								contact.value === "contact1"
									? "100%"
									: "auto",
						}}
					>
						<Box
							sx={{
								overflow: "hidden",
								height:
									creator.navigationType === "Drawer" &&
									contact.value === "contact1"
										? "100%"
										: "auto",
							}}
						>
							<img
								src={Map}
								alt="Map"
								style={{
									width: "100%",
									height:
										contact.value === "contact1"
											? "100%"
											: creator.navigationType === "Drawer"
											? 350
											: 320,
									objectFit: "cover",
								}}
							/>
						</Box>
						{contact.value === "contact2" && (
							<Box
								sx={{ height: "100px", textAlign: "center", marginTop: "4px" }}
							>
								<Typography variant="body2" sx={{ fontSize: "8px" }}>
									{contactData.address}
								</Typography>
							</Box>
						)}
					</Box>
				)}

			{contact && contact.value === "contact3" && (
				<Box padding={2} sx={{}}>
					<Typography
						variant="body2"
						sx={{ fontSize: "11px", fontWeight: "bold", textAlign: "center" }}
					>
						{t("contact.getContact")}
					</Typography>
					{creator.inputType === "input2" && (
						<Box sx={{ marginTop: 2 }}>
							<CustomTextField
								label={t("contact.message")}
								type="outline"
								multiline
							/>
						</Box>
					)}
					{creator.inputType === "input3" && (
						<Box sx={{ marginTop: 2 }}>
							<CustomTextField
								label={t("contact.message")}
								type="filled"
								multiline
							/>
						</Box>
					)}
					{creator.inputType === "input1" && (
						<Box sx={{ marginTop: 2 }}>
							<CustomTextField
								label={t("contact.message")}
								type="standard"
								multiline
							/>
						</Box>
					)}
					<Button
						variant="contained"
						fullWidth
						sx={{
							backgroundColor: creator ? creator.color : "#45A049",
							"&:hover": {
								backgroundColor: creator ? creator.color : "#45A049",
							},
							color: creator && creator.whiteTextButton ? "#ffffff" : "#000000",
							fontSize: "8px",
							borderRadius: creator && creator.buttonRounded ? "24px" : "0px",
							fontWeight: "bold",
							textTransform: "uppercase",
							height: "28px",
							width: "100%",
						}}
					>
						{t("contact.send")}
					</Button>
				</Box>
			)}

			{contact &&
				(contact.value === "contact4" ||
					contact.value === "contact5" ||
					contact.value === "contact6") && (
					<>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								padding: 2,
							}}
						>
							<Typography
								variant="body1"
								sx={{
									mt: 1,
									fontWeight: "bold",
									fontSize: "8px",
									textAlign: "center",
									mb: 1,
								}}
							>
								{contactData.address}
							</Typography>
							{contactData.hours.map((hour, index) => (
								<Typography
									key={index}
									variant="body2"
									sx={{ mt: 0.5, fontSize: "8px" }}
								>
									{hour}
								</Typography>
							))}
							<Box sx={{ display: "flex", gap: 1, mt: 3 }}>
								<Box
									aria-label="facebook"
									sx={{
										borderRadius: "50%",
										backgroundColor: "#3b5998",
										color: "#ffffff",
										width: 25,
										height: 25,
										aspectRatio: 1,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<FacebookIcon sx={{ fontSize: 13 }} />
								</Box>
								<Box
									aria-label="instagram"
									sx={{
										borderRadius: "50%",
										backgroundColor: "#405DE6",
										color: "#ffffff",
										width: 25,
										height: 25,
										aspectRatio: 1,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<InstagramIcon sx={{ fontSize: 13 }} />
								</Box>
								<Box
									aria-label="x"
									sx={{
										borderRadius: "50%",
										backgroundColor: "#000000",
										color: "#ffffff",
										width: 25,
										height: 25,
										aspectRatio: 1,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<TwitterIcon sx={{ fontSize: 13 }} />
								</Box>
								<Box
									aria-label="pinterest"
									sx={{
										borderRadius: "50%",
										backgroundColor: "#E60023",
										color: "#ffffff",
										width: 25,
										height: 25,
										aspectRatio: 1,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<PinterestIcon sx={{ fontSize: 13 }} />
								</Box>
							</Box>
						</Box>
						{(contact.value === "contact5" || contact.value === "contact6") && (
							<Box
								sx={{
									overflow: "hidden",
									height: 127,
									padding: contact.value === "contact6" ? 2 : 0,
								}}
							>
								<img
									src={Map}
									alt="Map"
									style={{
										width: "100%",
										height: 127,
										objectFit: "cover",
									}}
								/>
							</Box>
						)}
					</>
				)}

			{contact && contact.value === "contact7" && (
				<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							alignItems: "space-between",
							padding: 2,
							flex: 1,
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "flex-start",
								padding: 2,
								flex: 1,
							}}
						>
							<Typography
								variant="body1"
								sx={{
									mt: 1,
									fontWeight: "bold",
									fontSize: "8px",
									textAlign: "center",
									mb: 1,
								}}
							>
								{contactData.address}
							</Typography>
							{contactData.hours.map((hour, index) => (
								<Typography
									key={index}
									variant="body2"
									sx={{ mt: 0.5, fontSize: "8px" }}
								>
									{hour}
								</Typography>
							))}
						</Box>
					</Box>
					<Box sx={{ display: "flex" }}>
						<Box
							aria-label="facebook"
							sx={{
								backgroundColor: "#3b5998",
								color: "#ffffff",
								width: "25%",
								height: "auto",
								aspectRatio: "1",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<FacebookIcon sx={{ fontSize: 13 }} />
						</Box>
						<Box
							aria-label="instagram"
							sx={{
								backgroundColor: "#405DE6",
								color: "#ffffff",
								width: "25%",
								height: "auto",
								aspectRatio: "1",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<InstagramIcon sx={{ fontSize: 13 }} />
						</Box>
						<Box
							aria-label="x"
							sx={{
								backgroundColor: "#000000",
								color: "#ffffff",
								width: "25%",
								height: "auto",
								aspectRatio: "1",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<TwitterIcon sx={{ fontSize: 13 }} />
						</Box>
						<Box
							aria-label="pinterest"
							sx={{
								backgroundColor: "#E60023",
								color: "#ffffff",
								width: "25%",
								height: "auto",
								aspectRatio: "1",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<PinterestIcon sx={{ fontSize: 13 }} />
						</Box>
					</Box>
				</>
			)}

			{contact && contact.value === "contact8" && (
				<>
					<Box
						sx={{
							overflow: "hidden",
							height: 80,
							padding: contact.value === "contact6" ? 2 : 0,
						}}
					>
						<img
							src={Map}
							alt="Map"
							style={{
								width: "100%",
								height: 80,
								objectFit: "cover",
							}}
						/>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							padding: 2,
						}}
					>
						<Typography
							variant="body1"
							sx={{
								mt: 1,
								fontWeight: "bold",
								fontSize: "8px",
								textAlign: "center",
								mb: 1,
							}}
						>
							{contactData.address}
						</Typography>
						{contactData.hours.map((hour, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{ mt: 0.5, fontSize: "8px" }}
							>
								{hour}
							</Typography>
						))}
						<Box sx={{ display: "flex", gap: 1, mt: 3 }}>
							<Box
								aria-label="facebook"
								sx={{
									borderRadius: "50%",
									backgroundColor: "#3b5998",
									color: "#ffffff",
									width: 25,
									height: 25,
									aspectRatio: 1,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<FacebookIcon sx={{ fontSize: 13 }} />
							</Box>
							<Box
								aria-label="instagram"
								sx={{
									borderRadius: "50%",
									backgroundColor: "#405DE6",
									color: "#ffffff",
									width: 25,
									height: 25,
									aspectRatio: 1,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<InstagramIcon sx={{ fontSize: 13 }} />
							</Box>
							<Box
								aria-label="x"
								sx={{
									borderRadius: "50%",
									backgroundColor: "#000000",
									color: "#ffffff",
									width: 25,
									height: 25,
									aspectRatio: 1,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<TwitterIcon sx={{ fontSize: 13 }} />
							</Box>
							<Box
								aria-label="pinterest"
								sx={{
									borderRadius: "50%",
									backgroundColor: "#E60023",
									color: "#ffffff",
									width: 25,
									height: 25,
									aspectRatio: 1,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<PinterestIcon sx={{ fontSize: 13 }} />
							</Box>
						</Box>
						<Box
							sx={{
								display: "flex",
								width: "100%",
								mt: 4,
								flexDirection: "column",
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontSize: "11px",
									fontWeight: "bold",
									textAlign: "center",
								}}
							>
								{t("contact.getContact")}
							</Typography>
							{creator.inputType === "input2" && (
								<Box sx={{ marginTop: 2, width: "100%" }}>
									<CustomTextField
										label={t("contact.message")}
										type="outline"
										multiline
									/>
								</Box>
							)}
							{creator.inputType === "input3" && (
								<Box sx={{ marginTop: 2, width: "100%" }}>
									<CustomTextField
										label={t("contact.message")}
										type="filled"
										multiline
									/>
								</Box>
							)}
							{creator.inputType === "input1" && (
								<Box sx={{ marginTop: 2, width: "100%" }}>
									<CustomTextField
										label={t("contact.message")}
										type="standard"
										multiline
									/>
								</Box>
							)}
							<Button
								variant="contained"
								fullWidth
								sx={{
									backgroundColor: creator ? creator.color : "#45A049",
									"&:hover": {
										backgroundColor: creator ? creator.color : "#45A049",
									},
									color:
										creator && creator.whiteTextButton ? "#ffffff" : "#000000",
									fontSize: "8px",
									borderRadius:
										creator && creator.buttonRounded ? "24px" : "0px",
									fontWeight: "bold",
									textTransform: "uppercase",
									height: "28px",
									width: "100%",
								}}
							>
								{t("contact.send")}
							</Button>
						</Box>
					</Box>
				</>
			)}
		</Box>
	);
};

export default ShowcaseContact;
